import { createContext, useContext, useEffect, useSyncExternalStore, type ReactNode } from "react";
import { I18nextProvider } from "react-i18next";
import type { AccountId, PreferencesController, PreferencesState } from "./preferences";
import type { PreviewController, PreviewState } from "./preview";
import type { LocalizationRuntime, LocalizationSnapshot, MessageParams } from "./runtime";

const RuntimeContext = createContext<LocalizationRuntime | null>(null);

function navigatorLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}

/**
 * Provides an existing runtime (created once at module scope) to the tree
 * and starts loading the release. It never creates an i18next instance,
 * never keys or remounts its children and owns no state of its own, so a
 * catalog activation or language switch only re-renders the components
 * that read translations; routes, forms, sessions and payment state stay.
 */
export function LocalizationProvider({ runtime, browserLanguages, children }: { runtime: LocalizationRuntime; browserLanguages?: readonly string[]; children: ReactNode }) {
  useEffect(() => {
    // Idempotent: StrictMode's second effect run and later renders reuse the first start.
    void runtime.start({ browser: browserLanguages ?? navigatorLanguages() });
  }, [runtime, browserLanguages]);
  return (
    <RuntimeContext.Provider value={runtime}>
      <I18nextProvider i18n={runtime.i18n}>{children}</I18nextProvider>
    </RuntimeContext.Provider>
  );
}

export interface Localization extends LocalizationSnapshot {
  /**
   * Stable for the runtime's lifetime, so it can never restart an effect.
   * The component re-renders on every activation through the snapshot
   * subscription; do not cache translated text keyed only on `t`.
   */
  readonly t: (key: string, params?: MessageParams) => string;
  readonly setLanguage: LocalizationRuntime["setLanguage"];
  readonly refresh: LocalizationRuntime["refresh"];
}

/**
 * Translations of the provided runtime, or of `fallback` outside a provider
 * (component tests render the embedded English catalog that way).
 */
export function useLocalization(fallback?: LocalizationRuntime): Localization {
  const runtime = useContext(RuntimeContext) ?? fallback;
  if (!runtime) throw new Error("useLocalization needs a LocalizationProvider or a fallback runtime");
  const snapshot = useSyncExternalStore(runtime.subscribe, runtime.getSnapshot, runtime.getSnapshot);
  return { ...snapshot, t: runtime.t, setLanguage: runtime.setLanguage, refresh: runtime.refresh };
}

export interface Preferences extends Omit<PreferencesState, "save"> {
  readonly save: PreferencesController["save"];
  readonly reload: PreferencesController["reload"];
  /** State of the latest save request (saved only once the server confirmed it). */
  readonly saveState: PreferencesState["save"];
}

/** The signed-in account's language preference, for account settings controls. */
export function usePreferences(controller: PreferencesController): Preferences {
  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  const { save: saveState, ...rest } = state;
  return { ...rest, saveState, save: controller.save, reload: controller.reload };
}

/**
 * Feeds the server's identity projection to the controller: the account id,
 * null when signed out, undefined while the projection has not answered
 * (then nothing changes). Mount it once, inside the app's auth state.
 * `refetchIdentity` is the app's existing refetch of that projection; it is
 * called once when the controller finds the browser session no longer
 * belongs to the account shown, so the next projection decides (no sign-out).
 */
export function usePreferencesAccount(
  controller: PreferencesController,
  accountId: AccountId | null | undefined,
  refetchIdentity?: () => void,
): void {
  const status = useSyncExternalStore(controller.subscribe, () => controller.getSnapshot().status, () => controller.getSnapshot().status);
  useEffect(() => {
    if (accountId !== undefined) controller.setAccount(accountId);
  }, [controller, accountId]);
  useEffect(() => {
    if (status === "account_changed") refetchIdentity?.();
  }, [status, refetchIdentity]);
}

export interface Preview extends PreviewState {
  readonly enter: PreviewController["enter"];
  readonly exit: PreviewController["exit"];
  /** Reads the account's preview access again (Retry after unavailable). */
  readonly discover: PreviewController["discover"];
}

/**
 * The tester translation preview, for account language controls. Opening
 * them reads the account's preview access once (no polling); a changed
 * account reads it again. Controls stay hidden unless the server admitted
 * this account.
 */
export function usePreview(controller: PreviewController): Preview {
  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  const { accountId } = state;
  useEffect(() => {
    if (accountId != null) void controller.discover();
  }, [controller, accountId]);
  return { ...state, enter: controller.enter, exit: controller.exit, discover: controller.discover };
}
